import produce, { enableMapSet } from "immer";
import { create } from "zustand";
import { mountStoreDevtool } from "simple-zustand-devtools";
import { DrawioInterface } from "../DrawioClient/DrawioInterface";
import { CustomizedDrawioClient } from "../DrawioClient/CustomizedDrawioClient";
import {
  currentPathChangeHandler,
  setCellTags,
  toggleSingleCellVisibility,
  setRepoData,
} from "./StoreHandlers";
import { updateRepoDataInCodeCanvasIndexedDB } from "../utils/CodeCanvasIndexedDB";
import {
  compareNewWikiToSavedWiki,
  compareRefSimulationsToCached,
  compareRefCellToPathToCached,
  handleModifiedWikis,
} from "../Components/Header/StatusBar/StatusBarUtils";
import {
  INITIAL_MODIFIED_SIMULATION_STATE,
  INITIAL_SIMULATIONS_STATE,
} from "../Components/SourceDoc/TabViews/SimulationsTab/SimulationsTab";
import { SnackbarKey } from "notistack";
import { toggleCellsVisibility } from "../Components/SourceDoc/TabViews/SimulationsTab/SimulationUtils";
import sendGTMStateChangeEvent, {
  manageGoogleAnalyticsConsent,
} from "../utils/GTMHelper";
import { anonymize } from "../utils/AnonymizationHelper";
import { IRecentlyOpenedEntry } from "../Components/LandingPage/RecentFilesDropdown";
import {
  handleRecordStateChange,
  saveRecordedActions,
  startRecordingActions,
} from "./UndoManager";
import FlexSearch from "flexsearch";
import { RepoTree } from "../utils/RepoTree";
import { processUsecaseDescription } from "../utils/MarkdownProcessor";
import { createFileMetadata } from "../utils/TokenCounter";
import { logger } from "../utils/Logger";

// Enable Immer's Map/Set support
enableMapSet();

interface RepoItem {
  path: string;
  type: "tree" | "blob";
  fileName: string;
  contents?: string[];
}

const initialState: Store = {
  anonymizedMetadata: {
    username: "",
    currentRepo: "",
    currentBranch: "",
  },
  hasShownWelcomeDialog: false,
  session: {
    mode: "unauthenticatedGithub",
    user: {
      username: "",
    },
    isLoggedIn: false,
    local: {
      diagramFile: {
        handle: null,
        fileName: null,
        fileXML: "",
      },
      directory: {
        handle: null,
      },
      hasFSAccess:
        "chooseFileSystemEntries" in window || "showOpenFilePicker" in window,
      isMac: navigator.userAgent.includes("Mac OS X"),
    },
  },
  debugMode: false,
  startDemo: false,
  drawioInitCompleted: false,
  URLParamParsingDone: false,
  showLandingComponent: false,
  privateRepositoryPrompt: false,
  showGitHubLoginSpotlight: false,
  lastDrawioEventName: "",
  landingPageLoaded: false,
  doLoadDiagram: false,
  isNewDiagramFileCreated: false,
  currentRepo: "",
  currentBranch: "",
  selectedModel: "gemini-2.5-pro",
  regenerateWithPro: false,
  currentRepoMetadata: { owner: "" },
  currentPath: null,
  repoData: {},
  simulationMappings: {},
  isRetrievingRepoData: false,
  isUserChoosingDirectory: false,
  reposList: [],
  selectedTab: 3,
  selectedCell: "",
  multiSelect: false,
  cellToPath: {}, // cellId: path
  branchesList: [],
  simulationsState: INITIAL_SIMULATIONS_STATE,
  currentUnsavedSimulationObject: INITIAL_MODIFIED_SIMULATION_STATE,
  unsavedSimulationsStateChanges: [],
  fileStatus: {
    isDiagramModified: false,
    isWikiModified: false,
    modifiedWikis: {},
    modifiedSimulations: { added: {}, removed: {}, modified: {} },
    isSimulationsModified: false,
    modifiedCellToPath: { added: {}, removed: {}, modified: {} },
    isCellToPathModified: false,
  },
  diagramData: {
    drawioXML: "",
    fileName: "",
    fileURL: "",
  },
  referenceDiagramFile: {
    drawioXML: "",
    fileName: "",
    fileURL: "",
    repoData: {},
    repo: "",
    branch: "",
    owner: "",
    wiki: {},
    simulations: {},
    cellToPath: {},
  },
  drawioInterface: null,
  drawioClient: null,
  notification: { loading: "", error: "", success: "" },
  dialog: { type: "", data: null },
  deleteMode: null,
  pausePathChangeToDrawio: false,
  pausePathChangeToCodeCanvas: false,
  openFolderOnDoubleClickFlag: false,
  showCellToFolderParentPath: false,
  fuse: null,
  searchResults: null,
  selectedText: "",
  recentlyOpenedList: [],
  flags: {
    isChangingPath: false,
    isHandlingSelectionChange: false,
    isAddingNewSimStep: false,
  },
  recordStateChange: "STOP_AND_DISCARD",
  isPerformingUndoRedo: false,
  ongoingUndoRedoActionsRecordings: [],
  undoRedoState: {
    undo: false,
    redo: false,
  },
  checkRunImpactedCells: [],
  videoTutorialSection: "",
  hideCellsToggleNotificationId: null,
  showHideIrrelevantCellsToggle: false,
  fileIndex: null,
  documentMap: new Map(),
  indexingProgress: {
    total: 0,
    current: 0,
    isIndexing: false,
  },
  downloadProgress: {
    phase: "idle",
    bytesDownloaded: 0,
    totalBytes: 0,
    filesProcessed: 0,
    totalFiles: 0,
    isDownloading: false,
  },
  searchValue: "",
  hideIrrelevantCells: false,
  considerParentsAsRelevantCells: true,
  selectionStartLine: null,
  selectionEndLine: null,
  isCellCreationRequestInitiatedFromCodeCanvas: false,
  repoTree: null,
  ignorePatterns: [],
  usecaseFeatures: null,
  onboardingSuggestedFiles: null,
  triggerOnboardingWorkflow: false,
  onboardingWorkflowState: {
    step1: {
      name: "Repository Indexing & Tree Generation",
      isStarted: false,
      isCompleted: false,
      startTime: null,
      endTime: null,
    },
    step2: {
      name: "Essential Files Analysis",
      isStarted: false,
      isCompleted: false,
      startTime: null,
      endTime: null,
    },
    step3: {
      name: "Use-case Features Analysis",
      isStarted: false,
      isCompleted: false,
      startTime: null,
      endTime: null,
    },
  },
  statsWidgetVisible: (() => {
    // If user has explicitly set a preference in sessionStorage, use that
    const sessionStorageValue = sessionStorage.getItem("statsWidgetVisible");
    if (sessionStorageValue !== null) {
      return sessionStorageValue !== "false";
    }

    // Otherwise, only show by default if debugMode=verbose is set in URL params
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const debugMode = urlParams.get("debugMode");
      return debugMode === "verbose";
    } catch (error) {
      // If URL parsing fails, default to false
      return false;
    }
  })(),
  autoRepoDiagramConfig: {
    enabled: false,
    maxDepth: 3,
    generateFoldersOnly: true,
    maxCells: 300,
  },
  autoRepoDiagramProgress: {
    total: 0,
    current: 0,
    isGenerating: false,
    currentLevel: 0,
    totalLevels: 0,
    currentFolderPath: "",
    currentFolderName: "",
    currentCells: 0,
  },
  isSimulationFullSizeMode: false,
  isRunningSimulation: false,
  isInsertingSimulationCells: false,
  showOnboardingModelSelectionDialog: false,
  userEventCount: 0,
  userManuallyToggledSDMaximize: false,
  isMaxSD: false,
  shouldShowSimulationOnboarding: false,
  isSimulationOnboardingActive: false,
  firstAISimulationName: "",
  floatingStepDescriptionVisible: false,
};

const store = (set) => ({
  ...initialState,
  setUserEventCount: (userEventCount: number) =>
    set(
      produce((state: Store) => {
        state.userEventCount = userEventCount;
      })
    ),
  incrementUserEventCount: () =>
    set(
      produce((state: Store) => {
        state.userEventCount += 1;
      })
    ),
  setUserManuallyToggledSDMaximize: (toggled: boolean) =>
    set(
      produce((state: Store) => {
        state.userManuallyToggledSDMaximize = toggled;
      })
    ),
  setIsMaxSD: (isMaxSD: boolean) =>
    set(
      produce((state: Store) => {
        state.isMaxSD = isMaxSD;
      })
    ),
  setShouldShowSimulationOnboarding: (show: boolean) =>
    set(
      produce((state: Store) => {
        state.shouldShowSimulationOnboarding = show;
      })
    ),
  setIsSimulationOnboardingActive: (active: boolean) =>
    set(
      produce((state: Store) => {
        state.isSimulationOnboardingActive = active;
      })
    ),
  setFirstAISimulationName: (name: string) =>
    set(
      produce((state: Store) => {
        state.firstAISimulationName = name;
      })
    ),
  setFloatingStepDescriptionVisible: (visible: boolean) =>
    set(
      produce((state: Store) => {
        state.floatingStepDescriptionVisible = visible;
      })
    ),
  setUsecaseFeatures: async (features: UsecaseFeature[] | null) => {
    // First update the usecaseFeatures state
    set(
      produce((state: Store) => {
        state.usecaseFeatures = features;
      })
    );

    // Convert usecases to simulations and add them natively using setSimulations for proper persistence
    if (features && features.length > 0) {
      const { simulationsState, setSimulations } = useStore.getState();
      const currentSimulations = simulationsState.simulations;
      const newAISimulations: Simulations = {};

      features.forEach((usecase) => {
        // Skip the __RAW__ entry that might be used for debugging
        if (usecase.usecaseTitle === "__RAW__") {
          return;
        }

        const simulationName = usecase.usecaseTitle;
        const simulation: Simulation = {
          name: simulationName,
          simSteps: [], // Usecases don't have simsteps initially
          description: processUsecaseDescription(usecase.usecaseDescription),
          simulationNodesAndEdges: {},
          isAIGenerated: true,
          keywords: usecase.usecaseKeywords,
        };

        newAISimulations[simulationName] = simulation;
      });

      // Merge with existing simulations using setSimulations for proper persistence
      const mergedSimulations = {
        ...currentSimulations,
        ...newAISimulations,
      };

      // Use setSimulations to ensure proper caching and persistence
      await setSimulations(mergedSimulations);

      // Automatically select the first AI-generated simulation
      const firstSimulationKey = Object.keys(newAISimulations)[0];
      if (firstSimulationKey) {
        useStore.getState().setSelectedSimulationKey(firstSimulationKey);
        // Store the first AI simulation name and set onboarding flags
        useStore.getState().setFirstAISimulationName(firstSimulationKey);
        useStore.getState().setShouldShowSimulationOnboarding(true);
        // Note: The simulation onboarding tutorial will be triggered later
        // in SimulationsTab.tsx after the simulation is completely generated and inserted
        logger.debug.info(
          `OnboardingWorkflow: Automatically selected first simulation and set onboarding flags: ${firstSimulationKey}`
        );
      }
    }
  },
  setOnboardingSuggestedFiles: (files: any) =>
    set(
      produce((state: Store) => {
        state.onboardingSuggestedFiles = files;
      })
    ),
  setLoggedIn: (isLoggedIn: boolean) =>
    set(
      produce((state: Store) => {
        state.session.isLoggedIn = isLoggedIn;
      })
    ),
  setDrawioInitCompleted: (initCompleted: boolean) =>
    set(
      produce((state: Store) => {
        state.drawioInitCompleted = initCompleted;
      })
    ),
  setLandingPageLoaded: (landingPageLoaded: boolean) =>
    set(
      produce((state: Store) => {
        state.landingPageLoaded = landingPageLoaded;
      })
    ),

  setDoLoadDiagram: (doLoadDiagram: boolean) =>
    set(
      produce((state: Store) => {
        state.doLoadDiagram = doLoadDiagram;
      })
    ),
  setUsername: (username: string) => {
    useStore
      .getState()
      .setAnonymizedMetadata({ username: anonymize(username) });
    return set(
      produce((state: Store) => {
        state.session.user.username = username;
      })
    );
  },
  setCurrentRepo: (repo: string, repoMetadata: RepoMetadata = null) => {
    useStore.getState().setAnonymizedMetadata({ currentRepo: anonymize(repo) });
    return set(
      produce((state: Store) => {
        state.currentRepo = repo;
        state.currentRepoMetadata = repoMetadata;
      })
    );
  },
  setCurrentBranch: (branch: string) => {
    useStore
      .getState()
      .setAnonymizedMetadata({ currentBranch: anonymize(branch) });
    return set(
      produce((state: Store) => {
        state.currentBranch = branch;
      })
    );
  },
  setSelectedModel: (model: string) =>
    set(
      produce((state: Store) => {
        state.selectedModel = model;
      })
    ),
  setRegenerateWithPro: (regenerateWithPro: boolean) =>
    set(
      produce((state: Store) => {
        state.regenerateWithPro = regenerateWithPro;
      })
    ),
  setRepoData: async (repo: Record<string, RepoItem>, doLoadDiagram = true) => {
    return await setRepoData(set, repo, doLoadDiagram);
  },
  setCellToPath: (cellToPath: ICellToPath) => {
    compareRefCellToPathToCached(cellToPath);
    set(
      produce((state: Store) => {
        state.cellToPath = cellToPath;
      })
    );
  },
  setIsRetrievingRepoData: (
    isRetrievingRepoData: boolean,
    showNotification: boolean = true
  ) => {
    useStore
      .getState()
      .setLoadingNotification(
        isRetrievingRepoData && showNotification
          ? "Retrieving repository content"
          : ""
      );
    set(
      produce((state: Store) => {
        state.isRetrievingRepoData = isRetrievingRepoData;
      })
    );
  },
  setSelectedTab: (newTab: number) => {
    return set(
      produce((state: Store) => {
        state.selectedTab = newTab;
      })
    );
  },
  setRepoObjectEntry: (path, data) => {
    const currentRepoData = useStore.getState().repoData[path]
      ? { [path]: useStore.getState().repoData[path] }
      : {};
    const newRepoData = { [path]: data };
    setCellTags(currentRepoData, newRepoData);
    set(
      produce((state: Store) => {
        state.repoData = {
          ...state.repoData,
          [path]: {
            ...state.repoData[path],
            ...data,
          },
        };
      })
    );
  },
  setNewRepoObject: (newPath, newObject) => {
    const currentRepoData = {};
    const newRepoData = { [newPath]: newObject };
    setCellTags(currentRepoData, newRepoData);
    set(
      produce((state: Store) => {
        state.repoData[newPath] = newObject;
      })
    );
  },
  deleteRepoObject: (path) => {
    const currentRepoData = { path: useStore.getState().repoData[path] };
    const newRepoData = {};
    setCellTags(currentRepoData, newRepoData);
    set(
      produce((state: Store) => {
        delete state.repoData[path];
      })
    );
  },
  setDeleteMode: (deleteMode: "saveWiki" | "deleteWiki") => {
    set(
      produce((state: Store) => {
        state.deleteMode = deleteMode;
      })
    );
  },
  setPausePathChangeToDrawio: (pausePathChangeToDrawio: boolean) => {
    set(
      produce((state: Store) => {
        // we do this to avoid conflicts between the two modes
        if (pausePathChangeToDrawio && state.pausePathChangeToCodeCanvas) {
          state.pausePathChangeToCodeCanvas = false;
        }
        state.pausePathChangeToDrawio = pausePathChangeToDrawio;
      })
    );
  },
  setPausePathChangeToCodeCanvas: (pausePathChangeToCodeCanvas: boolean) => {
    set(
      produce((state: Store) => {
        // we do this to avoid conflicts between the two modes
        if (pausePathChangeToCodeCanvas && state.pausePathChangeToDrawio) {
          state.pausePathChangeToDrawio = false;
        }
        state.pausePathChangeToCodeCanvas = pausePathChangeToCodeCanvas;
      })
    );
  },
  setRepoObjectAttribute: (path, attr, value) => {
    const currentRepoObject = { ...useStore.getState().repoData[path] };
    const currentRepoData = { [path]: currentRepoObject };
    const newRepoObject = { ...currentRepoObject, [attr]: value };
    const newRepoData = { [path]: newRepoObject };
    setCellTags(currentRepoData, newRepoData);
    set(
      produce((state: Store) => {
        state.repoData[path][attr] = value;
      })
    );
  },
  setRepoObjectAttributes: (path, repoObjectAttributes) => {
    const currentRepoObject = { ...useStore.getState().repoData[path] };
    const currentRepoData = { [path]: currentRepoObject };
    const newRepoObject = { ...currentRepoObject, ...repoObjectAttributes };
    const newRepoData = { [path]: newRepoObject };
    setCellTags(currentRepoData, newRepoData);
    set(
      produce((state: Store) => {
        state.repoData[path] = {
          ...state.repoData[path],
          ...repoObjectAttributes,
        };
      })
    );
  },

  deleteRepoObjectAttribute: (path, attr) => {
    const currentRepoObject = { ...useStore.getState().repoData[path] };
    const currentRepoData = { [path]: currentRepoObject };
    let newRepoObject = { ...currentRepoObject };
    delete newRepoObject[attr];
    const newRepoData = { [path]: newRepoObject };
    setCellTags(currentRepoData, newRepoData);
    set(
      produce((state: Store) => {
        delete state.repoData[path][attr];
      })
    );
  },
  deleteRepoObjectAttributes: (path, attrs) => {
    let currentRepoObject = { ...useStore.getState().repoData[path] };
    let newRepoObject = { ...currentRepoObject };

    attrs.forEach((attr) => {
      if (attr in newRepoObject) {
        delete newRepoObject[attr];
      }
    });
    const currentRepoData = { [path]: currentRepoObject };
    const newRepoData = { [path]: newRepoObject };
    setCellTags(currentRepoData, newRepoData);
    set(
      produce((state: Store) => {
        attrs.forEach((attr) => {
          delete state.repoData[path][attr];
        });
      })
    );
  },
  deleteChild: (deletionPath, childId) => {
    let deletionChildren = useStore.getState().repoData[deletionPath]?.children;
    if (deletionChildren) {
      let children = [...deletionChildren];
      let deleteField = false;
      let filteredChildren = [];
      if (Array.isArray(children)) {
        filteredChildren = children.filter((id) => id !== childId);
        if (filteredChildren?.length === 0) {
          deleteField = true;
        }
      }
      set(
        produce((state: Store) => {
          if (deleteField) delete state.repoData[deletionPath]?.children;
          else state.repoData[deletionPath].children = filteredChildren;
        })
      );
    }
  },
  setNewCellToPath: (cellId, path) => {
    const cellToPath = useStore.getState().cellToPath;
    compareRefCellToPathToCached({ ...cellToPath, [cellId]: path });
    set(
      produce((state: Store) => {
        state.cellToPath[cellId] = path;
      })
    );
  },
  deleteCellToPath: (path) => {
    set(
      produce((state: Store) => {
        delete state.cellToPath[path];
      })
    );
    let cellToPath = useStore.getState().cellToPath;
    compareRefCellToPathToCached(cellToPath);
  },
  setSelectedCell: (newCellId: string) => {
    set(
      produce((state: Store) => {
        state.selectedCell = newCellId;
      })
    );
  },
  setMultiSelect: (multiSelect: boolean) => {
    set(
      produce((state: Store) => {
        state.multiSelect = multiSelect;
      })
    );
  },
  setFileContent: (path: string, content: Object) => {
    // Create metadata if content is a string
    let metadata = null;
    if (typeof content === "string") {
      metadata = createFileMetadata(content);
    }

    return set(
      produce((state: Store) => {
        state.repoData[path].fileContent = content;
        // Add metadata if we have it
        if (metadata) {
          state.repoData[path].fileMetadata = metadata;
          state.repoData[path].tokenCount = metadata.tokenCount;
          state.repoData[path].byteSize = metadata.byteSize;
          state.repoData[path].exceedsThresholds = metadata.exceedsThresholds;
        }
      })
    );
  },
  setRepoTree: (repoTree: RepoTree) =>
    set(
      produce((state: Store) => {
        state.repoTree = repoTree;
      })
    ),
  setIsUserChoosingDirectory: (isUserChoosingDirectory: boolean) =>
    set(
      produce((state: Store) => {
        state.isUserChoosingDirectory = isUserChoosingDirectory;
      })
    ),
  setWikiContent: async (path: string, content: string) => {
    const currentWiki = useStore.getState().repoData[path]?.wiki;
    // if wiki hasn't changed, don't do anything
    if (
      (!currentWiki && !content) ||
      useStore.getState().repoData[path]?.wiki === content
    )
      return;
    await startRecordingActions("SET_WIKI");
    const currentRepoObject = { ...useStore.getState().repoData[path] };
    const currentRepoData = { [path]: currentRepoObject };
    const newRepoObject = { ...currentRepoObject, wiki: content };
    const newRepoData = { [path]: newRepoObject };
    compareNewWikiToSavedWiki(path, content);
    await setCellTags(currentRepoData, newRepoData);
    set(
      produce((state: Store) => {
        if (content === "") {
          delete state.repoData[path].wiki;
        } else {
          state.repoData[path].wiki = content;
        }
      })
    );
    await updateRepoDataInCodeCanvasIndexedDB();
    await saveRecordedActions("SET_WIKI");
  },
  setCurrentPath: (
    path: string,
    silentChange: boolean = false,
    isDrawioEventOrigin = false
  ) => {
    if (useStore.getState().pausePathChangeToDrawio) silentChange = true;

    // Clear temporary line highlighting from previous path if it exists
    const prevPath = useStore.getState().currentPath;
    if (prevPath && prevPath !== path) {
      const prevRepoData = useStore.getState().repoData[prevPath];
      if (
        prevRepoData &&
        (prevRepoData.tempStartLine || prevRepoData.tempEndLine)
      ) {
        set(
          produce((state: Store) => {
            if (state.repoData[prevPath]) {
              delete state.repoData[prevPath].tempStartLine;
              delete state.repoData[prevPath].tempEndLine;
            }
          })
        );
      }
    }

    if (
      useStore.getState()?.repoData[useStore.getState().currentPath]
        ?.visible === false &&
      path !== useStore.getState().currentPath
    )
      toggleSingleCellVisibility(
        useStore.getState().repoData[useStore.getState().currentPath].cellId,
        false
      );
    set(
      produce((state: Store) => {
        state.currentPath = path;
      })
    );
    currentPathChangeHandler(silentChange, isDrawioEventOrigin);
  },
  setReposList: (reposList: Repository[]) =>
    set(
      produce((state: Store) => {
        state.reposList = reposList;
      })
    ),
  setBranchesList: (branchesList: Branch[]) =>
    set(
      produce((state: Store) => {
        state.branchesList = branchesList;
      })
    ),
  setDiagramModified: (isDiagramModified: boolean) =>
    set(
      produce((state: Store) => {
        state.fileStatus.isDiagramModified = isDiagramModified;
      })
    ),
  setModifiedWikis: (action: string, modifiedWiki: Object) =>
    handleModifiedWikis(set, action, modifiedWiki),
  setModifiedCellToPath: (
    modifiedCellToPath: ModifiedCellToPath,
    isCellToPathModified: boolean
  ) => {
    set(
      produce((state: Store) => {
        state.fileStatus.modifiedCellToPath = modifiedCellToPath;
        state.fileStatus.isCellToPathModified = isCellToPathModified;
      })
    );
  },
  setDrawioXML: (drawioXML: string) =>
    set(
      produce((state: Store) => {
        state.diagramData.drawioXML = drawioXML;
      })
    ),
  setDrawioInterface: (drawioInterface: DrawioInterface) =>
    set(
      produce((state: Store) => {
        state.drawioInterface = drawioInterface;
      })
    ),
  setDrawioClient: (drawioClient: CustomizedDrawioClient) =>
    set(
      produce((state: Store) => {
        state.drawioClient = drawioClient;
      })
    ),
  setLoadingNotification: (loadingMessage: string) =>
    set(
      produce((state: Store) => {
        state.notification.loading = loadingMessage;
      })
    ),
  setErrorNotification: (errorMessage: string) => {
    sendGTMStateChangeEvent("errorNotification", errorMessage);
    return set(
      produce((state: Store) => {
        state.notification.error = errorMessage;
      })
    );
  },
  setSuccessNotification: (successMessage: string) => {
    sendGTMStateChangeEvent("successNotification", successMessage);
    return set(
      produce((state: Store) => {
        state.notification.success = successMessage;
      })
    );
  },
  postToDrawio: (action: Action) => {
    const drawioClient = useStore.getState().drawioClient;
    if (!drawioClient) {
      return;
    }
    drawioClient.sendAction(action);
  },
  postToDrawioWaitForResponse: async (action: Action) => {
    const drawioClient = useStore.getState().drawioClient;
    if (!drawioClient) {
      return;
    }
    const drawioResponse = await drawioClient.sendActionWaitForResponse(action);
    if (drawioResponse.status === "ERROR") {
      throw new Error(drawioResponse);
    } else {
      return drawioResponse;
    }
  },
  setDialog: (dialogType: string, dialogData: DialogDataObj = null) => {
    set(
      produce((state: Store) => {
        state.dialog.type = dialogType;
        state.dialog.data = dialogData ? dialogData : null;
      })
    );
  },
  setDiagramData: (newData: DiagramData) =>
    set(
      produce((state: Store) => {
        state.diagramData = { ...state.diagramData, ...newData };
      })
    ),
  setDiagramFileMetadata: (newData: FileMetadataObj) =>
    set(
      produce((state: Store) => {
        state.session.local.diagramFile = {
          ...state.session.local.diagramFile,
          ...newData,
        };
      })
    ),
  setLocalDirectoryMetaData: (newData: DirectoryMetadataObj) =>
    set(
      produce((state: Store) => {
        state.session.local.directory = {
          ...state.session.local.directory,
          ...newData,
        };
      })
    ),
  setSessionMode: (mode: SessionMode) => {
    manageGoogleAnalyticsConsent(mode);
    sendGTMStateChangeEvent("sessionMode", mode);
    return set(
      produce((state: Store) => {
        state.session.mode = mode;
      })
    );
  },
  setReferenceDiagramFile: (fileContent: DiagramFile) =>
    set(
      produce((state: Store) => {
        state.referenceDiagramFile = fileContent;
      })
    ),
  setIsNewDiagramFileCreated: (isNewDiagramFileCreated: boolean) =>
    set(
      produce((state: Store) => {
        state.isNewDiagramFileCreated = isNewDiagramFileCreated;
      })
    ),
  setSimulations: async (simulations: Simulations) => {
    await set(
      produce((state: Store) => {
        state.simulationsState.simulations = simulations;
      })
    );

    await compareRefSimulationsToCached(simulations);
    await updateRepoDataInCodeCanvasIndexedDB();
  },
  setCurrentUnsavedSimulationObject: async (
    currentUnsavedSimulationObject: Simulation
  ) => {
    set(
      produce((state: Store) => {
        state.currentUnsavedSimulationObject = currentUnsavedSimulationObject;
      })
    );
  },
  setModifiedSimulations: (
    modifiedSimulations: ModifiedSimulations,
    isSimulationsModified: boolean
  ) => {
    set(
      produce((state: Store) => {
        state.fileStatus.modifiedSimulations = modifiedSimulations;
        state.fileStatus.isSimulationsModified = isSimulationsModified;
      })
    );
  },
  setIsEditingSimulations: async (isEditingSimulations: string) => {
    return await set(
      produce((state: Store) => {
        state.simulationsState.isEditingSimulations = isEditingSimulations;
      })
    );
  },
  setSimulationsState: (simulationsState: SimulationsState) =>
    set(
      produce((state: Store) => {
        state.simulationsState = simulationsState;
      })
    ),
  setSelectedSimulationKey: (selectedSimulationKey: string) =>
    set(
      produce((state: Store) => {
        state.simulationsState.selectedSimulationKey = selectedSimulationKey;
        useStore.getState().setSelectedCell("");

        // When closing a simulation (selectedSimulationKey is empty), reset editing and running states
        if (!selectedSimulationKey) {
          state.simulationsState.isEditingSimulations = "";
          state.simulationsState.currentStep = "";
          // Note: isRunning is a local state in SimulationsTab, not in the store
        }
      })
    ),
  setCellsSetToCorrespondingVisiblityState: (
    cellsSetToCorrespondingVisiblityState: boolean
  ) =>
    set(
      produce((state: Store) => {
        state.simulationsState.cellsSetToCorrespondingVisiblityState =
          cellsSetToCorrespondingVisiblityState;
      })
    ),
  setOpenFolderOnDoubleClickFlag: (flag: boolean) =>
    set(
      produce((state: Store) => {
        state.openFolderOnDoubleClickFlag = flag;
      })
    ),
  setShowCellToFolderParentPath: (flag: boolean) =>
    set(
      produce((state: Store) => {
        state.showCellToFolderParentPath = flag;
      })
    ),
  setFuse: (fuse: any) =>
    set(
      produce((state: Store) => {
        state.fuse = fuse;
      })
    ),
  addToFuse: (paths: any) => {
    paths.forEach((path) => {
      let repoDataToAdd = useStore.getState().repoData[path];
      useStore.getState().fuse.add(repoDataToAdd);
    });
  },
  removeFromFuse: (paths: any) => {
    useStore.getState().fuse.remove((doc) => {
      return paths.includes(doc.path);
    });
  },
  setSearchResults: (searchResults: any) => {
    if (
      searchResults === null &&
      !useStore.getState().simulationsState.selectedSimulationKey
    ) {
      toggleCellsVisibility(false);
    }
    set(
      produce((state: Store) => {
        state.searchResults = searchResults;
      })
    );
  },
  setSelectedText: (selectedText: string) =>
    set(
      produce((state: Store) => {
        state.selectedText = selectedText;
      })
    ),
  setDebugMode: (debugMode: "debug" | "verbose") =>
    set(
      produce((state: Store) => {
        state.debugMode = debugMode;
      })
    ),
  setStartDemo: (demoState: boolean) =>
    set(
      produce((state: Store) => {
        state.startDemo = demoState;
      })
    ),
  // used to avoid infinit loops like sell selection flickering
  setlastDrawioEventName: (drawioEventName: string) =>
    set(
      produce((state: Store) => {
        state.lastDrawioEventName = drawioEventName;
      })
    ),

  setIsRunningSimulationNotifId: (isRunningSimulationNotifId: SnackbarKey) =>
    set(
      produce((state: Store) => {
        state.simulationsState.isRunningSimulationNotifId =
          isRunningSimulationNotifId;
      })
    ),
  setHideCellsToggleNotificationId: (
    hideCellsToggleNotificationId: SnackbarKey
  ) =>
    set(
      produce((state: Store) => {
        state.hideCellsToggleNotificationId = hideCellsToggleNotificationId;
      })
    ),
  setShowHideIrrelevantCellsToggle: (showHideIrrelevantCellsToggle: boolean) =>
    set(
      produce((state: Store) => {
        state.showHideIrrelevantCellsToggle = showHideIrrelevantCellsToggle;
      })
    ),
  setCellsWithUnsavedVisibilityFlag: (
    cellsWithUnsavedVisibilityFlag: CellsWithUnsavedVisibilityFlag
  ) =>
    set(
      produce((state: Store) => {
        state.simulationsState.cellsWithUnsavedVisibilityFlag =
          cellsWithUnsavedVisibilityFlag;
      })
    ),
  setFlag: (flagName: FlagNames, flagValue: boolean) =>
    set(
      produce((state: Store) => {
        state.flags[flagName] = flagValue;
      })
    ),
  setRecentlyOpenedList: (
    recentlyOpenedList: [string, IRecentlyOpenedEntry][]
  ) =>
    set(
      produce((state: Store) => {
        state.recentlyOpenedList = recentlyOpenedList;
      })
    ),
  setShowLandingComponent: (showLandingComponent: boolean) =>
    set(
      produce((state: Store) => {
        state.showLandingComponent = showLandingComponent;
      })
    ),
  setAnonymizedMetadata: (anonymizedMetadata: AnonymizedMetadata) =>
    set(
      produce((state: Store) => {
        state.anonymizedMetadata = {
          ...state.anonymizedMetadata,
          ...anonymizedMetadata,
        };
      })
    ),
  setHasShownWelcomeDialog: (hasShownWelcomeDialog: boolean) =>
    set(
      produce((state: Store) => {
        state.hasShownWelcomeDialog = hasShownWelcomeDialog;
      })
    ),
  setRecordStateChange: async (
    recordStateChange: RecordStateChange,
    trackedAction: TrackedActions
  ) => {
    return await handleRecordStateChange(
      recordStateChange,
      trackedAction,
      set,
      produce
    );
  },
  setIsPerformingUndoRedo: (isPerformingUndoRedo: boolean) =>
    set(
      produce((state: Store) => {
        state.isPerformingUndoRedo = isPerformingUndoRedo;
      })
    ),
  setOngoingUndoRedoActionsRecordings: (
    ongoingUndoRedoActionsRecordings: TrackedActions[]
  ) =>
    set(
      produce((state: Store) => {
        state.ongoingUndoRedoActionsRecordings =
          ongoingUndoRedoActionsRecordings;
      })
    ),
  setUndoRedoState: (undoRedoState: UndoRedoState) =>
    set(
      produce((state: Store) => {
        state.undoRedoState = undoRedoState;
      })
    ),
  setUnsavedSimulationsStateChanges: (
    unsavedSimulationsStateChanges: StateChange[]
  ) => {
    set(
      produce((state: Store) => {
        state.unsavedSimulationsStateChanges = unsavedSimulationsStateChanges;
      })
    );
  },
  setCheckRunImpactedCells: (checkRunImpactedCells: CheckRunImpactedCell[]) =>
    set(
      produce((state: Store) => {
        state.checkRunImpactedCells = checkRunImpactedCells;
      })
    ),
  setURLParamParsingDone: (URLParamParsingDone: boolean) =>
    set(
      produce((state: Store) => {
        state.URLParamParsingDone = URLParamParsingDone;
      })
    ),
  setVideoTutorialSection: (videoTutorialSection: VideoTutorialSection) =>
    set(
      produce((state: Store) => {
        state.videoTutorialSection = videoTutorialSection;
      })
    ),
  setFileIndex: (fileIndex: FlexSearch.Index) =>
    set(
      produce((state: Store) => {
        state.fileIndex = fileIndex;
      })
    ),
  setDownloadProgress: (downloadProgress: DownloadProgress) =>
    set(
      produce((state: Store) => {
        state.downloadProgress = downloadProgress;
      })
    ),
  setHideIrrelevantCells: (hideIrrelevantCells: boolean) =>
    set(
      produce((state: Store) => {
        state.hideIrrelevantCells = hideIrrelevantCells;
      })
    ),
  setConsiderParentsAsRelevantCells: (
    considerParentsAsRelevantCells: boolean
  ) =>
    set(
      produce((state: Store) => {
        state.considerParentsAsRelevantCells = considerParentsAsRelevantCells;
      })
    ),
  loadSimulationByName: (simulationName: string) =>
    set(
      produce((state: Store) => {
        if (
          state.simulationsState.simulations &&
          simulationName in state.simulationsState.simulations
        ) {
          state.simulationsState.selectedSimulationKey = simulationName;
          state.currentUnsavedSimulationObject =
            state.simulationsState.simulations[simulationName];
          state.selectedTab = 3; // Switch to simulations tab
        }
      })
    ),
  transitionToNextSimulation: (nextSimulationKey: string) => {
    const nextSimulation =
      useStore.getState().simulationsState.simulations[nextSimulationKey];

    // Update all simulation-related state in one action
    set((state) => ({
      simulationsState: {
        ...state.simulationsState,
        selectedSimulationKey: nextSimulationKey,
        currentStep: nextSimulation?.simSteps?.[0]?.simStepId || "",
        isRunning: true,
      },
      currentUnsavedSimulationObject: nextSimulation,
    }));
  },
  setSelectionStartLine: (selectionStartLine: number | null) =>
    set(
      produce((state: Store) => {
        state.selectionStartLine = selectionStartLine;
      })
    ),
  setSelectionEndLine: (selectionEndLine: number | null) =>
    set(
      produce((state: Store) => {
        state.selectionEndLine = selectionEndLine;
      })
    ),
  // Enhanced scroll function trigger for simulations
  triggerEnhancedCodeScroll: () => {
    // This function can be called from simulation components to trigger enhanced scrolling
    // The actual implementation is in CodeTab component, this is just a trigger mechanism
    const event = new CustomEvent("enhancedCodeScroll", {
      detail: { timestamp: Date.now() },
    });
    window.dispatchEvent(event);
  },
  setIsCellCreationRequestInitiatedFromCodeCanvas: (
    isCellCreationRequestInitiatedFromCodeCanvas: boolean
  ) =>
    set(
      produce((state: Store) => {
        state.isCellCreationRequestInitiatedFromCodeCanvas =
          isCellCreationRequestInitiatedFromCodeCanvas;
      })
    ),
  setIgnorePatterns: (patterns: string[]) =>
    set(
      produce((state: Store) => {
        state.ignorePatterns = patterns || [];
      })
    ),
  setTriggerOnboardingWorkflow: (trigger: boolean) =>
    set(
      produce((state: Store) => {
        state.triggerOnboardingWorkflow = trigger;
      })
    ),
  setOnboardingWorkflowState: (newState: Partial<OnboardingWorkflowState>) =>
    set(
      produce((state: Store) => {
        state.onboardingWorkflowState = {
          ...state.onboardingWorkflowState,
          ...newState,
        };
      })
    ),
  resetOnboardingWorkflowState: () =>
    set(
      produce((state: Store) => {
        state.onboardingWorkflowState = {
          step1: {
            name: "Repository Indexing & Tree Generation",
            isStarted: false,
            isCompleted: false,
            startTime: null,
            endTime: null,
          },
          step2: {
            name: "Essential Files Analysis",
            isStarted: false,
            isCompleted: false,
            startTime: null,
            endTime: null,
          },
          step3: {
            name: "Use-case Features Analysis",
            isStarted: false,
            isCompleted: false,
            startTime: null,
            endTime: null,
          },
        };
      })
    ),
  setStatsWidgetVisible: (visible: boolean) => {
    sessionStorage.setItem("statsWidgetVisible", visible.toString());
    return set(
      produce((state: Store) => {
        state.statsWidgetVisible = visible;
      })
    );
  },
  setAutoRepoDiagramConfig: (config: Partial<AutoRepoDiagramConfig>) =>
    set(
      produce((state: Store) => {
        state.autoRepoDiagramConfig = {
          ...state.autoRepoDiagramConfig,
          ...config,
        };
      })
    ),
  setAutoRepoDiagramProgress: (progress: Partial<AutoRepoDiagramProgress>) =>
    set(
      produce((state: Store) => {
        state.autoRepoDiagramProgress = {
          ...state.autoRepoDiagramProgress,
          ...progress,
        };
      })
    ),
  setPrivateRepositoryPrompt: (privateRepositoryPrompt: boolean) =>
    set(
      produce((state: Store) => {
        state.privateRepositoryPrompt = privateRepositoryPrompt;
      })
    ),
  setShowGitHubLoginSpotlight: (showGitHubLoginSpotlight: boolean) =>
    set(
      produce((state: Store) => {
        state.showGitHubLoginSpotlight = showGitHubLoginSpotlight;
      })
    ),
  setIsSimulationFullSizeMode: (isSimulationFullSizeMode: boolean) =>
    set(
      produce((state: Store) => {
        state.isSimulationFullSizeMode = isSimulationFullSizeMode;
      })
    ),
  setIsRunningSimulation: (isRunningSimulation: boolean) =>
    set(
      produce((state: Store) => {
        state.isRunningSimulation = isRunningSimulation;
      })
    ),
  setIsInsertingSimulationCells: (isInsertingSimulationCells: boolean) =>
    set(
      produce((state: Store) => {
        state.isInsertingSimulationCells = isInsertingSimulationCells;
      })
    ),
  setShowOnboardingModelSelectionDialog: (
    showOnboardingModelSelectionDialog: boolean
  ) =>
    set(
      produce((state: Store) => {
        state.showOnboardingModelSelectionDialog =
          showOnboardingModelSelectionDialog;
      })
    ),
  setSimulationMappings: (simulationMappings: RepoDataSimulationMappings) =>
    set(
      produce((state: Store) => {
        state.simulationMappings = simulationMappings;
      })
    ),
  addSimulationMapping: (path: string, mapping: SimulationMapping) =>
    set(
      produce((state: Store) => {
        if (!state.simulationMappings[path]) {
          state.simulationMappings[path] = [];
        }
        // Check if mapping already exists to avoid duplicates
        const exists = state.simulationMappings[path].some(
          (m) =>
            m.simulationName === mapping.simulationName &&
            m.simStepId === mapping.simStepId
        );
        if (!exists) {
          state.simulationMappings[path].push(mapping);
        }
      })
    ),
  removeSimulationMapping: (
    path: string,
    simulationName: string,
    simStepId: string
  ) =>
    set(
      produce((state: Store) => {
        if (state.simulationMappings[path]) {
          state.simulationMappings[path] = state.simulationMappings[
            path
          ].filter(
            (m) =>
              !(
                m.simulationName === simulationName && m.simStepId === simStepId
              )
          );
          // Clean up empty arrays
          if (state.simulationMappings[path].length === 0) {
            delete state.simulationMappings[path];
          }
        }
      })
    ),
  clearSimulationMappings: () =>
    set(
      produce((state: Store) => {
        state.simulationMappings = {};
      })
    ),
});

const useStore = create(store);

if (process.env.NODE_ENV === "development") {
  mountStoreDevtool("Store", useStore);
}

export default useStore;
